'use client';

import { useCommendaOSEntitySchema } from '@commenda/api/commenda-os-dsl';
import {
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@commenda/kakashi';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import { SchemaFieldPicker } from '../../editor/SchemaFieldPicker';
import type { CommendaOSFieldConfig, DataSourceConfig, FormDSL } from '@commenda/types';
import { ContentLibraryEditor } from './ContentLibraryEditor';
import { FormFieldSourceEditor } from './FormFieldSourceEditor';
import { ContextBindingEditor } from './ContextBindingEditor';
import {
  getContextBindingEditorKeys,
  getContextSourceFields,
} from './context-helpers';

export function DataSourceEditor({
  dataSource,
  onChange,
  currentFieldId,
  dsl,
  defaultExpanded = false,
}: {
  dataSource?: DataSourceConfig;
  onChange: (dataSource: DataSourceConfig | undefined) => void;
  currentFieldId?: string;
  dsl?: FormDSL;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(
    defaultExpanded || dataSource?.type === COMMENDA_OS_DATA_SOURCE
  );

  const sourceType = dataSource?.type ?? 'static';

  const handleSourceTypeChange = (type: string) => {
    if (type === 'static') {
      onChange(undefined);
    } else if (type === 'api') {
      onChange({
        type: 'api',
        endpoint: dataSource?.endpoint ?? '',
        searchable: dataSource?.searchable ?? false,
      });
    } else if (type === 'content-library') {
      onChange({
        type: 'content-library',
        contentLibrary: dataSource?.contentLibrary ?? {
          contentId: '',
          inputs: [],
        },
      });
    } else if (type === 'form-field') {
      onChange({
        type: 'form-field',
        formField: dataSource?.formField ?? {
          moduleId: '',
          labelFields: [],
        },
      });
    } else {
      onChange({
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: dataSource?.commendaOS ?? {
          entity: 'corporation',
          field: { read: '' },
          mode: 'read-write',
        },
      });
    }
  };

  const updateCommendaOS = (updates: Partial<CommendaOSFieldConfig>) => {
    if (dataSource?.type === COMMENDA_OS_DATA_SOURCE && dataSource.commendaOS) {
      onChange({
        ...dataSource,
        commendaOS: {
          ...dataSource.commendaOS,
          ...updates,
        },
      });
    }
  };

  const contextSourceFields = useMemo(() => {
    if (!dsl || !dataSource?.commendaOS?.entity) return [];
    return getContextSourceFields(
      dsl,
      currentFieldId,
      dataSource.commendaOS.entity
    );
  }, [dsl, currentFieldId, dataSource?.commendaOS?.entity]);

  const formContextKeys = useMemo(() => {
    const requirements = (
      dsl as unknown as {
        form?: { contextRequirements?: { required?: string[] } };
      }
    )?.form?.contextRequirements;
    return requirements?.required || [];
  }, [dsl]);

  const entity = dataSource?.commendaOS?.entity;
  const { entitySchema } = useCommendaOSEntitySchema(entity);

  const schemaCanWrite = useMemo(() => {
    if (!entitySchema || !dataSource?.commendaOS?.field?.read) return false;

    const targetPath = dataSource.commendaOS.field.read;

    const findFieldWritability = (
      fields: Record<string, unknown>,
      parentPath: string = ''
    ): boolean =>
      Object.entries(fields).some(([key, field]) => {
        const f = field as Record<string, unknown>;
        const readConfig = f.read as { path?: string } | undefined;
        const currentPath = parentPath ? `${parentPath}.${key}` : key;

        const hasUpdate = !!(f.update as { path?: string })?.path;
        const hasCreate = !!(f.create as { path?: string })?.path;

        if (currentPath === targetPath) return hasUpdate || hasCreate;
        if (readConfig?.path === targetPath) return hasUpdate || hasCreate;

        if (f.type === 'object' && f.fields) {
          return findFieldWritability(
            f.fields as Record<string, unknown>,
            currentPath
          );
        }
        return false;
      });

    return findFieldWritability(entitySchema.fields as Record<string, unknown>);
  }, [entitySchema, dataSource?.commendaOS?.field?.read]);

  return (
    <div className="space-y-4">
      <button
        type="button"
        className="flex items-center gap-2 w-full text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-text-tertiary" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-tertiary" />
        )}
        <h3 className="txt-small-medium text-text-secondary">Data Source</h3>
        {sourceType === COMMENDA_OS_DATA_SOURCE && (
          <Badge variant="gray" className="ml-auto">
            Commenda OS
          </Badge>
        )}
      </button>

      {isExpanded && (
        <div className="space-y-4 pl-6 border-l-2 border-border-secondary">
          <div className="space-y-2">
            <Label>Source Type</Label>
            <Select
              value={sourceType}
              onValueChange={(value) => handleSourceTypeChange(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="static">Static (Manual Options)</SelectItem>
                <SelectItem value="api">API Endpoint</SelectItem>
                <SelectItem value="content-library">Content Library</SelectItem>
                <SelectItem value="form-field">
                  Entries from a repeatable module
                </SelectItem>
                <SelectItem value="commenda-os">Commenda OS</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {sourceType === 'api' && (
            <>
              <div className="space-y-2">
                <Label>API Endpoint</Label>
                <Input
                  value={dataSource?.endpoint ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...dataSource,
                      type: 'api',
                      endpoint: e.target.value,
                    })
                  }
                  placeholder="/api/..."
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Searchable</Label>
                <Switch
                  checked={dataSource?.searchable ?? false}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...dataSource,
                      type: 'api',
                      searchable: checked,
                    })
                  }
                />
              </div>
            </>
          )}

          {sourceType === 'form-field' && (
            <FormFieldSourceEditor
              dataSource={dataSource}
              onChange={onChange}
              dsl={dsl}
              currentFieldId={currentFieldId}
            />
          )}

          {sourceType === 'content-library' && (
            <ContentLibraryEditor
              dataSource={dataSource}
              onChange={onChange}
              dsl={dsl}
              currentFieldId={currentFieldId}
            />
          )}

          {sourceType === COMMENDA_OS_DATA_SOURCE && dataSource?.commendaOS && (
            <>
              <div className="space-y-2">
                <Label>Entity</Label>
                <Select
                  value={dataSource.commendaOS.entity}
                  onValueChange={(value) => updateCommendaOS({ entity: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corporation">Corporation</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="transferPricingPolicy">
                      Transfer Pricing Policy
                    </SelectItem>
                    <SelectItem value="transferPricingBenchmark">
                      Transfer Pricing Benchmark
                    </SelectItem>
                    <SelectItem value="director" disabled>
                      Director (Coming Soon)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Field</Label>
                <SchemaFieldPicker
                  entity={dataSource.commendaOS.entity}
                  value={dataSource.commendaOS.field?.read}
                  onSelect={(path) =>
                    updateCommendaOS({
                      field: {
                        ...dataSource.commendaOS!.field,
                        read: path,
                      },
                    })
                  }
                  placeholder="Select a field..."
                />
              </div>

              {/* Mode Selection - Uses live schema to determine available modes */}
              <div className="space-y-2">
                <Label>Mode</Label>
                <Select
                  value={dataSource.commendaOS.mode}
                  onValueChange={(value) =>
                    updateCommendaOS({
                      mode: value as 'read' | 'read-write',
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">Read Only</SelectItem>
                    {schemaCanWrite && (
                      <SelectItem value="read-write">Read & Write</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {schemaCanWrite ? (
                  <p className="txt-xs-regular text-text-tertiary">
                    Read Only: Display value but don&apos;t allow editing. Read
                    & Write: Display and save changes.
                  </p>
                ) : (
                  <p className="txt-xs-regular text-text-tertiary">
                    This field is read-only and cannot be updated after
                    creation.
                  </p>
                )}
              </div>

              {/* Context Binding */}
              <ContextBindingEditor
                entity={dataSource.commendaOS.entity}
                contextKeys={
                  entity
                    ? getContextBindingEditorKeys(entitySchema, entity)
                    : []
                }
                contextBindings={dataSource.commendaOS.contextBindings}
                availableFields={contextSourceFields}
                formContextKeys={formContextKeys}
                onChange={(bindings) =>
                  updateCommendaOS({ contextBindings: bindings })
                }
              />

              <div className="p-3 bg-bg-secondary rounded-md border border-border-secondary">
                <p className="txt-xs-regular text-text-tertiary">
                  <Info className="inline-block w-3 h-3 mr-1" />
                  This field will be connected to Commenda OS. Ensure the form
                  has the required context (companyId, corporationId)
                  configured.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
